import { QueryFactory, SecuritySettingsDto } from '@lmt/shared-types';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import styles from '@lmt/unicorn-shared/app/components/tenant-settings/components/SettingsBlock.module.scss';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import {
  maxRule,
  minRule,
} from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useEffect } from 'react';
import generalStyles from './SettingsBlock.module.scss';
import { useEventCallback } from '@mui/material';
import { handleSubmitFormError } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { TenantSettingsKeys } from './types';
import { useRegisterUnsavedChanges } from './useRegisterUnsavedChanges';

export const AutoLogoutSettings: FC = (props) => {
  const i18n = useScopedTranslation('tenant_settings.auto_logout_settings');
  const modals = useModal();
  const {
    data: settings,
    refetch: refetch,
    isLoading: isLoading,
  } = QueryFactory.SecuritySettingsQuery.useGetQuery();

  const form = useAdvancedForm<SecuritySettingsDto>(
    useEventCallback(async (data: SecuritySettingsDto) => {
      try {
        const dto: SecuritySettingsDto = { ...data };
        await QueryFactory.SecuritySettingsClient.patch(dto);
        await refetch();
      } catch (error) {
        const errorDetails = handleSubmitFormError(error, setError);
        if (errorDetails.overallServerError) {
          await modals.showError({ text: errorDetails.overallServerError });
        }
      }
    }),
    settings,
    {
      mode: 'onChange',
      shouldUnregister: false,
    },
  );

  const { formState, reset, register, setError } = form;

  const onReset = useEventCallback(() => {
    reset(settings);
  });

  useEffect(() => {
    onReset();
  }, [settings]);

  const isUnsavedChanges = useRegisterUnsavedChanges(
    TenantSettingsKeys.AutoLogout,
    form,
  );

  return (
    <Loading loading={isLoading}>
      <form onSubmit={form.handleSubmitDefault}>
        <div
          className={generalStyles.container}
          data-test-id="security-settings-container"
        >
          <div className={generalStyles.header}>
            <div className={generalStyles.row}>
              <div className={generalStyles.title}>{i18n.t('title')}</div>
            </div>
            <div className={generalStyles.optionContainer}>
              <Button
                disabled={!isUnsavedChanges}
                title={i18n.t('reset_button_title')}
                className={generalStyles.option}
                color={ButtonColor.Secondary}
                onClick={onReset}
              />
              <SubmitButton
                disabled={!isUnsavedChanges}
                submitting={formState.isSubmitting}
                className={generalStyles.option}
                title={i18n.t('submit_button_title')}
              />
            </div>
          </div>
          <div className={styles.dataContainer}>
            <div className={styles.blockContainer}>
              <Field
                title={i18n.t('admin_idle_logout_timeout')}
                className={styles['expression-field']}
              >
                <Input
                  variant="normal"
                  type="number"
                  step="1"
                  placeholder={i18n.t('disabled')}
                  {...register('adminLogoutTimeoutMinutes', {
                    ...minRule(1),
                    ...maxRule(30000),
                    setValueAs: (value) => {
                      return !value ? null : parseInt(value);
                    },
                  })}
                  errorText={
                    formState.errors.adminLogoutTimeoutMinutes?.message
                  }
                />
              </Field>
              <Field
                title={i18n.t('idle_logout_timeout')}
                className={styles['expression-field']}
              >
                <Input
                  variant="normal"
                  type="number"
                  step="1"
                  placeholder={i18n.t('disabled')}
                  {...register('idleLogoutTimeoutMinutes', {
                    ...minRule(1),
                    ...maxRule(30000),
                    setValueAs: (value) => {
                      return !value ? null : parseInt(value);
                    },
                  })}
                  errorText={formState.errors.idleLogoutTimeoutMinutes?.message}
                />
              </Field>
            </div>
          </div>
        </div>
      </form>
    </Loading>
  );
};
